import { useQuery } from '@tanstack/react-query';

import { readModelPrices } from '@/lib/api/generated/api';
import { UsageError, isForbidden } from '@/features/workspace-usage';

import { llmProfileKeys } from '../queryKeys';
import type { ModelPricesView } from '../types';

/**
 * The organization's current price per model, org-wide (spec 092 FR-007).
 *
 * Shaped like `useUnpricedModelRuns`, but never period-scoped and never gated behind
 * `enabled`: every model group on the LLM Connections & Models page needs to know its
 * own price state, not just the ones a period-scoped summary already flagged.
 *
 * Goes through the generated `readModelPrices` operation, so the 200 body arrives
 * already narrowed to `ModelPricesView` and no cast stands in for a response type.
 * An earlier draft called `customFetch` directly because the endpoint was not yet in
 * `openapi.json`; it is, and so this is the ordinary path every other read takes.
 */
export function useModelPrices() {
  return useQuery<ModelPricesView>({
    queryKey: llmProfileKeys.modelPrices(),
    queryFn: async ({ signal }) => {
      const response = await readModelPrices({ signal });
      if (response.status === 200) return response.data;
      if (response.status === 403) {
        throw new UsageError(
          'forbidden',
          'Only an organization administrator can see model prices.',
        );
      }
      throw new UsageError('unavailable', 'Could not find out model prices.');
    },
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => !isForbidden(error) && failureCount < 2,
  });
}
